/**
 * learning/rules.ts — declarative inference rules for the Learning Engine.
 *
 * Every preference dimension is inferred by ONE rule that reads the raw
 * behavioral event history and returns a value + evidence + plain-English
 * reasons. Rules are pure functions of (events, now) — deterministic and
 * individually testable. Adding a new learned dimension = adding one rule.
 */

import type {
  BehavioralAction,
  BehavioralEvent,
  BehaviorCategory,
  PreferenceKey,
  PreferenceValue,
} from './models';

/** How strongly each action counts as evidence (negative = counter-evidence). */
export const ACTION_WEIGHTS: Record<BehavioralAction, number> = {
  completed: 3,
  favorited: 3,
  saved: 2.5,
  visited: 2.5,
  repeated: 2,
  clicked: 1.5,
  compared: 1,
  viewed: 0.5,
  ignored: -1,
  dismissed: -1.5,
  skipped: -2,
};

/** Evidence loses half its weight every 90 days. */
const DECAY_HALF_LIFE_MS = 90 * 24 * 60 * 60 * 1000;
/** Evidence older than a year expires entirely — tastes from a past life
 * should stop informing inference (this also makes decay-only preference
 * transitions possible, since pure exponential decay preserves ordering). */
export const EVIDENCE_TTL_MS = 365 * 24 * 60 * 60 * 1000;

export function decayedWeight(event: BehavioralEvent, now: number): number {
  const age = Math.max(0, now - event.at);
  if (age > EVIDENCE_TTL_MS) return 0;
  const decay = Math.pow(0.5, age / DECAY_HALF_LIFE_MS);
  return ACTION_WEIGHTS[event.action] * decay;
}

export interface RuleResult {
  value: PreferenceValue;
  /** Total positive evidence weight — drives confidence. */
  evidence: number;
  supportingBehaviors: string[];
  reasons: string[];
}

export type InferenceRule = (events: BehavioralEvent[], now: number) => RuleResult | null;

// ─── Generic rule builders ─────────────────────────────────────────────────────

const describeAction: Record<BehavioralAction, string> = {
  viewed: 'viewed',
  clicked: 'selected',
  ignored: 'ignored',
  saved: 'saved',
  completed: 'completed',
  skipped: 'skipped',
  favorited: 'favorited',
  dismissed: 'dismissed',
  repeated: 'returned to',
  visited: 'visited',
  compared: 'compared',
};

function reasonsFromEvents(events: BehavioralEvent[], detail: string): string[] {
  const byAction = new Map<BehavioralAction, number>();
  for (const e of events) byAction.set(e.action, (byAction.get(e.action) ?? 0) + 1);
  return Array.from(byAction.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([action, count]) => `You ${describeAction[action]} ${detail} ${count} time${count === 1 ? '' : 's'}.`);
}

/**
 * Learn a categorical preference: the attribute option with the highest
 * (decayed, action-weighted) score wins. Negative actions push options down.
 */
export function categoricalRule(
  attribute: string,
  categories: BehaviorCategory[] | null,
  describe: (option: string) => string,
): InferenceRule {
  return (events, now) => {
    const scores = new Map<string, number>();
    const byOption = new Map<string, BehavioralEvent[]>();
    for (const e of events) {
      if (categories && !categories.includes(e.category)) continue;
      const option = e.attributes[attribute];
      if (typeof option !== 'string') continue;
      scores.set(option, (scores.get(option) ?? 0) + decayedWeight(e, now));
      const list = byOption.get(option) ?? [];
      list.push(e);
      byOption.set(option, list);
    }
    let best: string | null = null;
    let bestScore = 0;
    for (const [option, score] of scores) {
      if (score > bestScore) { best = option; bestScore = score; }
    }
    if (best == null || bestScore <= 0) return null;
    const supporting = (byOption.get(best) ?? []).filter((e) => ACTION_WEIGHTS[e.action] > 0);
    return {
      value: best,
      evidence: bestScore,
      supportingBehaviors: supporting.map((e) => e.id),
      reasons: reasonsFromEvents(supporting, describe(best)),
    };
  };
}

/**
 * Learn a numeric preference: weighted median of the attribute across
 * positively-signalled events (median resists outliers like one splurge).
 */
export function numericRule(
  attribute: string,
  categories: BehaviorCategory[] | null,
  describe: (value: number) => string,
  round: (v: number) => number = Math.round,
): InferenceRule {
  return (events, now) => {
    const samples: Array<{ value: number; weight: number; event: BehavioralEvent }> = [];
    for (const e of events) {
      if (categories && !categories.includes(e.category)) continue;
      const raw = e.attributes[attribute];
      if (typeof raw !== 'number') continue;
      const weight = decayedWeight(e, now);
      if (weight <= 0) continue; // only positive engagement informs numeric taste
      samples.push({ value: raw, weight, event: e });
    }
    if (samples.length === 0) return null;
    samples.sort((a, b) => a.value - b.value);
    const total = samples.reduce((s, x) => s + x.weight, 0);
    let acc = 0;
    let median = samples[samples.length - 1].value;
    for (const s of samples) {
      acc += s.weight;
      if (acc >= total / 2) { median = s.value; break; }
    }
    const value = round(median);
    return {
      value,
      evidence: total,
      supportingBehaviors: samples.map((s) => s.event.id),
      reasons: reasonsFromEvents(samples.map((s) => s.event), describe(value)),
    };
  };
}

// ─── Bespoke rules ─────────────────────────────────────────────────────────────

/** Nightlife: engagement ratio on nightlife-tagged events (positive vs negative). */
const nightlifeRule: InferenceRule = (events, now) => {
  const nightlife = events.filter(
    (e) => e.attributes['nightlife'] === true || (e.category === 'event' && e.attributes['eventType'] === 'nightlife'),
  );
  if (nightlife.length < 3) return null;
  const score = nightlife.reduce((s, e) => s + decayedWeight(e, now), 0);
  const positive = nightlife.filter((e) => ACTION_WEIGHTS[e.action] > 0);
  const negative = nightlife.filter((e) => ACTION_WEIGHTS[e.action] < 0);
  const value = score > 0 ? 'high' : 'low';
  const supporting = value === 'high' ? positive : negative;
  return {
    value,
    evidence: Math.abs(score),
    supportingBehaviors: supporting.map((e) => e.id),
    reasons: value === 'high'
      ? reasonsFromEvents(positive, 'nightlife events')
      : reasonsFromEvents(negative, 'nightlife recommendations'),
  };
};

/**
 * Travel pace from base changes: fast (<21d between bases), medium, slow.
 * Follows the global inference contract: pure function of (events, now) with
 * the standard decay — recent base changes weigh more, expired ones drop out.
 */
const travelPaceRule: InferenceRule = (events, now) => {
  const baseVisits = events
    .filter((e) => e.category === 'base' && e.action === 'visited' && decayedWeight(e, now) > 0)
    .sort((a, b) => a.at - b.at);
  if (baseVisits.length < 2) return null;
  let weightedGap = 0;
  let weightTotal = 0;
  for (let i = 1; i < baseVisits.length; i++) {
    const weight = decayedWeight(baseVisits[i], now); // recency of the later move
    weightedGap += (baseVisits[i].at - baseVisits[i - 1].at) * weight;
    weightTotal += weight;
  }
  if (weightTotal <= 0) return null;
  const avgDays = weightedGap / weightTotal / (24 * 60 * 60 * 1000);
  const value = avgDays < 21 ? 'fast' : avgDays < 60 ? 'medium' : 'slow';
  return {
    value,
    evidence: weightTotal,
    supportingBehaviors: baseVisits.map((e) => e.id),
    reasons: [
      `You changed your base ${baseVisits.length} times, averaging ${Math.round(avgDays)} days per stay.`,
    ],
  };
};

// ─── The rule registry (one per preference dimension) ─────────────────────────

export const INFERENCE_RULES: Record<PreferenceKey, InferenceRule> = {
  monthlyBudget: numericRule('priceMonthly', ['housing', 'city', 'base'], (v) => `options around $${v}/month`),
  climate: categoricalRule('climate', ['city', 'base'], (o) => `${o}-climate cities`),
  internetSpeed: numericRule('internetMbps', null, (v) => `places with ~${v} Mbps internet`),
  housingType: categoricalRule('housingType', ['housing'], (o) => `${o} listings`),
  citySize: categoricalRule('citySize', ['city', 'base'], (o) => `${o} cities`),
  beachVsMountain: categoricalRule('terrain', ['city', 'base'], (o) => `${o} destinations`),
  urbanVsRural: categoricalRule('setting', ['city', 'base', 'neighborhood'], (o) => `${o} areas`),
  nightlife: nightlifeRule,
  walkability: categoricalRule('walkability', null, (o) => `${o}-walkability places`),
  transit: categoricalRule('transitMode', null, (o) => `${o} transit options`),
  remoteWorkStyle: categoricalRule('noiseLevel', ['coworking', 'cafe'], (o) => `${o} workspaces`),
  travelPace: travelPaceRule,
  lengthOfStay: numericRule('stayDays', ['base', 'city'], (v) => `stays of about ${v} days`),
  safetyPriority: categoricalRule('safetyFocus', null, (o) => `${o}-safety options`),
  language: categoricalRule('language', null, (o) => `${o}-speaking destinations`),
  community: categoricalRule('communityType', null, (o) => `${o} community spots`),
};

/** Evidence → confidence (0–100): saturating curve, K = half-confidence point. */
export function evidenceToConfidence(evidence: number): number {
  const K = 8;
  return Math.max(0, Math.min(100, Math.round((evidence / (evidence + K)) * 100)));
}
